import { useEffect, useRef } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { BrandedEmptyState } from '../../components/index.js';
import { MarkupStage } from './MarkupStage.jsx';
import { useFeedbackMarkup } from './FeedbackMarkupProvider.jsx';
import './feedbackMarkup.redesign.css';

function workspaceRouteMode(pathname) {
  if (/^\/feedback\/[^/]+$/.test(pathname || '')) return 'view';
  return 'edit';
}

export function FeedbackWorkspacePage() {
  const { packageId } = useParams();
  const location = useLocation();
  const { state, dispatch, openPackage } = useFeedbackMarkup();
  const pkg = state.package;
  const routeMode = workspaceRouteMode(location.pathname);

  // Tracks whether the routed package has already been the active session on
  // this mount. Ending a session navigates away via a router transition, but
  // the reducer clear commits first. Reopening here would resurrect the
  // session the user just ended.
  const routedPackageWasActiveRef = useRef(false);
  if (pkg?.id && pkg.id === packageId) routedPackageWasActiveRef.current = true;

  useEffect(() => {
    if (packageId && (packageId !== pkg?.id || state.workspaceMode !== routeMode)) {
      if (!pkg && routedPackageWasActiveRef.current) return;
      openPackage(packageId, { openWorkspace: true, mode: routeMode });
      return;
    }
    if (!packageId && state.workspaceMode !== 'edit') {
      dispatch({ type: 'workspace/mode', mode: 'edit' });
    }
  }, [dispatch, openPackage, packageId, pkg, pkg?.id, routeMode, state.workspaceMode]);

  // A routed package that failed to open (403/404) must show the established
  // permission/not-found pattern, never a stale stage from another package.
  const routedPackageOpenFailed =
    Boolean(packageId) && pkg?.id !== packageId && state.historyStatus === 'error';
  if (routedPackageOpenFailed) {
    return (
      <section className="feedback-workspace-page fbmk-redesign" data-testid="feedback-workspace">
        <div className="fbmk-state" data-testid="feedback-workspace-open-error">
          <BrandedEmptyState
            icon="⚠"
            eyebrow="Feedback"
            tone="brick"
            title="This feedback package could not be opened."
            description={
              state.historyError || 'It may have been deleted, or you may not have access to it.'
            }
            action={
              <Link className="btn ghost sm" to="/feedback">
                Back to My Feedback
              </Link>
            }
          />
        </div>
      </section>
    );
  }

  if (state.stage) {
    // The editor remains exactly where it was. All editing chrome renders
    // inside MarkupStage; this page contributes only the routed shell.
    return (
      <section
        className="feedback-workspace-page fbmk-redesign editing"
        data-testid="feedback-workspace"
      >
        <MarkupStage />
      </section>
    );
  }

  // The legacy summary/management surface has been consolidated into
  // /feedback. An active stage is the only reason this legacy route renders:
  // local-only Resume sets the stage BEFORE navigating here
  // (openScreenForEditing), so a stage-less arrival always means management
  // intent (e.g. Done) and belongs on My Feedback.
  if (!packageId) {
    return <Navigate to="/feedback" replace />;
  }

  if (pkg?.id === packageId) {
    return <Navigate to="/feedback" replace />;
  }

  // Persisted editor/replay deep links stay mounted while openPackage hydrates
  // their stage. This is deliberately not a second management view.
  return (
    <section
      className="feedback-workspace-page fbmk-redesign"
      data-testid="feedback-workspace"
      aria-busy="true"
    >
      <span className="sr-only">Opening feedback editor…</span>
    </section>
  );
}
